import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropertyCard from '../../components/PropertyCard';
import { Button } from '../../components/ui/button';
import { getSavedProperties } from '../../lib/api';
import { Heart, Loader2 } from 'lucide-react';

export default function SavedProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    loadSaved();
  }, []);

  const loadSaved = async (cursor = null) => {
    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await getSavedProperties({ cursor, limit: 12 });
      const data = res.data;
      
      if (cursor) {
        setProperties(prev => [...prev, ...data.items]);
      } else {
        setProperties(data.items || []);
      }
      
      setNextCursor(data.next_cursor);
      setHasMore(data.has_more);
    } catch (err) {
      console.error('Failed to load saved properties:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleUnsave = (propertyId) => {
    // Remove from list optimistically
    setProperties(prev => prev.filter(p => p.id !== propertyId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="saved-properties-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Saved Properties</h1>
        <p className="text-slate-500 mt-1">Your favorite vacation rentals</p>
      </div>

      {properties.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-xl">
          <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-slate-900 mb-2">No saved properties yet</h2>
          <p className="text-slate-500 mb-6">
            Start exploring and save properties you love!
          </p>
          <Link to="/search">
            <Button data-testid="browse-btn">Browse Properties</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map(property => (
              <PropertyCard 
                key={property.id} 
                property={property}
                showSaveButton
                isSaved={true}
                onUnsave={handleUnsave}
              />
            ))}
          </div>

          {hasMore && (
            <div className="text-center pt-6">
              <Button
                variant="outline"
                onClick={() => loadSaved(nextCursor)}
                disabled={loadingMore}
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
